import { Project } from '@/types/project';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Flag, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSmartPreloading } from '@/hooks/useSmartPreloading';
import { useProjectsEditPermissions } from '@/hooks/maintenance/useProjectsEditPermissions';
import InlineProjectStatusCell from '@/components/projects/InlineProjectStatusCell';
import { useSmartPrefetch } from '@/hooks/useSmartPrefetch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PROJECT_MARKS, PROJECT_MARK_LABELS, PROJECT_MARK_BADGE_COLORS, ProjectMark } from '@/constants/projectMarks';
import { useAccessibleContractYears } from '@/hooks/useAccessibleContractYears';
import { LifecycleStage } from '@/constants/lifecycleStages';
import { usePermissionCheck } from '@/hooks/useConsolidatedPermissions';
import { useCheckPermission } from '@/hooks/useUserRoles';
import { buildQuantityLabel } from '@/utils/lifecycleQuantity';
import LifecycleStageBadge from '@/components/maintenance/LifecycleStageBadge';
import BuildInlineStageCell from '@/components/maintenance/BuildInlineStageCell';
import TenderInlineStageCell from '@/components/maintenance/TenderInlineStageCell';
import InlineNumberCell from '@/components/maintenance/InlineNumberCell';
import { useUpdateProjectBaseline } from '@/hooks/maintenance/useUpdateProjectBaseline';
import { getTransformedPublicUrl } from '@/utils/storageImages';
import { MAINTENANCE_PROJECTS_KEY, MAINTENANCE_STATS_KEY } from '@/lib/queryKeys/maintenanceKeys';

interface ProjectCardsProps {
  projects: Project[];
}

const STATUS_LABELS: Record<string, string> = {
  intake: 'Intake',
  tender: 'Tender',
  build: 'Build',
  defects: 'Defects',
  archive: 'Archive',
  service: 'Service',
  inactive: 'Inactive'
};

const STATUS_COLORS: Record<string, string> = {
  intake: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  tender: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  build: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  defects: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800',
  archive: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-800',
  service: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800',
  inactive: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800'
};

const COMPANY_COLORS: Record<string, string> = {
  'ENP': 'bg-primary/10 text-primary border-primary/20',
  'Solid': 'bg-accent/10 text-accent-foreground border-accent/20',
};

export const ProjectCards = ({ projects }: ProjectCardsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { preload, cleanup, isEnabled } = useSmartPreloading();
  const { prefetchProject } = useSmartPrefetch();
  const [localMarks, setLocalMarks] = useState<Record<string, string>>({});
  const [localYears, setLocalYears] = useState<Record<string, string>>({});
  const { data: contractYears = [] } = useAccessibleContractYears();
  const canSetMark = usePermissionCheck('PROJECTS_SET_MARK');
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: editMap = {} } = useProjectsEditPermissions(projectIds);
  const hasStatusKey = useCheckPermission('PROJECTS_EDIT_STATUS').data ?? false;
  const hasGfaKey = useCheckPermission('PROJECTS_EDIT_GFA').data ?? false;
  const mutation = useUpdateProjectBaseline();

  // Initialize local marks from projects - default to 'decent' if null
  useEffect(() => {
    const marks: Record<string, string> = {};
    projects.forEach(p => {
      marks[p.id] = p.project_mark || 'decent';
    });
    setLocalMarks(marks);
  }, [projects]);

  // Initialize local contract years from projects
  useEffect(() => {
    const years: Record<string, string> = {};
    projects.forEach(p => {
      years[p.id] = p.contract_year || '';
    });
    setLocalYears(years);
  }, [projects]);

  const handleMarkChange = async (projectId: string, mark: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const previousMark = localMarks[projectId];
    
    // Optimistic update
    setLocalMarks(prev => ({ ...prev, [projectId]: mark }));
    
    const { error } = await supabase
      .from('projects')
      .update({ project_mark: mark })
      .eq('id', projectId);
    
    if (error) {
      // Revert on error
      setLocalMarks(prev => ({ ...prev, [projectId]: previousMark }));
      toast.error('Không thể cập nhật mark');
    } else {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['projects-optimized-v2'] });
      queryClient.invalidateQueries({ queryKey: ['global-project-access'] });
      queryClient.invalidateQueries({ queryKey: [MAINTENANCE_PROJECTS_KEY] });
      toast.success('Đã cập nhật mark');
    }
  };

  const handleYearChange = async (projectId: string, year: string | null, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const previous = localYears[projectId];
    
    // Optimistic update
    setLocalYears(prev => ({ ...prev, [projectId]: year || '' }));
    
    const { error } = await supabase
      .from('projects')
      .update({ contract_year: year })
      .eq('id', projectId);
    
    if (error) {
      // Revert on error
      setLocalYears(prev => ({ ...prev, [projectId]: previous }));
      toast.error('Không thể cập nhật năm hợp đồng');
    } else {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['projects-optimized-v2'] });
      queryClient.invalidateQueries({ queryKey: ['global-project-access'] });
      queryClient.refetchQueries({ queryKey: ['global-project-access'], type: 'all' });
      queryClient.invalidateQueries({ queryKey: [MAINTENANCE_PROJECTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MAINTENANCE_STATS_KEY] });
      toast.success('Đã cập nhật năm hợp đồng');
    }
  };

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleMouseEnter = (project: Project) => {
    // Use both preloading strategies
    if (isEnabled) {
      preload({
        queryKey: ['project-detail', project.id],
        queryFn: async () => {
          const { data } = await supabase
            .from('projects')
            .select('id, project_name, description, image_url, status, project_type, project_group, company, is_active, created_at, updated_at')
            .eq('id', project.id)
            .single();
          return data;
        }
      });
    }
    
    // Smart prefetch with related data
    prefetchProject(project.id, { delay: 300 });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
      {projects.map((project) => (
        <Card 
          key={project.id} 
          tabIndex={0}
          role="link"
          className="group cursor-pointer touch-manipulation overflow-hidden rounded-2xl border border-border/60 border-b-2 border-b-primary/20 bg-gradient-to-br from-card via-card to-card/95 shadow-sm hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 hover:ring-2 hover:ring-primary/10 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 h-full flex flex-col"
          onClick={() => navigate(`/projects/${project.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/projects/${project.id}`);
            }
          }}
          onMouseEnter={() => handleMouseEnter(project)}
        >
          <CardContent className="p-0 flex flex-col flex-1">
            {/* Slot 0 — Thumbnail */}
            <div className="relative aspect-[4/3] w-full bg-gradient-to-br from-muted/30 via-muted/20 to-muted/10 border-b border-border/40 overflow-hidden flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <ProjectLogo imageUrl={project.image_url} projectName={project.project_name} />
            </div>

            {/* Content Section */}
            <div className="p-4 space-y-3 flex-1 flex flex-col">
              {/* Slot 1 — Project Name */}
              <h3 
                className="font-semibold text-sm leading-snug text-foreground line-clamp-2 min-h-[2.5rem] group-hover:text-primary transition-colors duration-200" 
                title={project.project_name}
              >
                {project.project_name}
              </h3>

              {/* Slot 2a — Status + Type */}
              <div className="h-6 flex items-center gap-1.5 overflow-hidden">
                <InlineProjectStatusCell project={project} canEdit={!!editMap[project.id] && hasStatusKey} />

                {project.project_type ? (
                  <Badge 
                    variant="secondary" 
                    className="text-[10px] px-2 py-0.5 rounded-md font-normal bg-secondary/40 text-secondary-foreground/80 truncate max-w-full"
                  >
                    {project.project_type}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-2 py-0.5 rounded-md font-normal border-dashed text-muted-foreground/50"
                  >
                    —
                  </Badge>
                )}
              </div>

              {/* Slot 2b — Project Group */}
              <div className="h-6 flex items-center">
                {project.project_group ? (
                  <Badge 
                    variant="outline" 
                    className="text-[10px] px-2 py-0.5 rounded-md font-normal text-muted-foreground/70 border-muted-foreground/20 truncate max-w-full"
                  >
                    {project.project_group}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-2 py-0.5 rounded-md font-normal border-dashed text-muted-foreground/50"
                  >
                    —
                  </Badge>
                )}
              </div>

              {/* Slot 3 — Lifecycle row (always reserved) */}
              <div className="h-6 flex items-center gap-1.5 flex-wrap">
                {project.status === 'build' ? (
                  <BuildInlineStageCell
                    projectId={project.id}
                    stage={project.lifecycle_stage_build ?? null}
                    editable
                  />
                ) : project.status === 'tender' ? (
                  <TenderInlineStageCell
                    projectId={project.id}
                    stage={project.lifecycle_stage_tender ?? null}
                    editable
                  />
                ) : project.status === 'service' && project.lifecycle_stage ? (
                  <>
                    <LifecycleStageBadge
                      stage={project.lifecycle_stage as LifecycleStage}
                      isOverridden={(project as any).lifecycle_stage_override != null}
                    />
                    <Badge
                      variant="outline"
                      className="text-[10px] px-2 py-0.5 rounded-md font-normal text-muted-foreground/80 border-muted-foreground/20"
                    >
                      {buildQuantityLabel(
                        project.lifecycle_stage as LifecycleStage,
                        project.cost_driver,
                        project.display_quantity_hour,
                        project.display_quantity_m2
                      )}
                    </Badge>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground/50">Chưa set</span>
                )}
              </div>

              {/* Slot 4 — GFA row */}
              <div className="h-6 flex items-center text-xs">
                <InlineNumberCell
                  value={project.gross_floor_area_m2 ?? null}
                  onSave={(newValue) =>
                    mutation.mutateAsync({
                      projectId: project.id,
                      field: 'gross_floor_area_m2',
                      value: newValue,
                    })
                  }
                  editable={!!editMap[project.id] && hasGfaKey}
                  min={0}
                  allowDecimal={true}
                  allowNull={true}
                  placeholder="—"
                  suffix=" m²"
                />
              </div>

              {/* Slot 5 — Mark + Company */}
              <div className="h-6 flex items-center gap-1.5 mt-auto">
                {canSetMark ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-0.5 rounded hover:bg-muted/50 transition-colors"
                        title="Set mark"
                      >
                        <Flag className={`h-3 w-3 ${
                          localMarks[project.id] === 'prio_1' ? 'text-red-500 fill-red-500' :
                          localMarks[project.id] === 'prio_2' ? 'text-orange-500 fill-orange-500' :
                          'text-muted-foreground/50'
                        }`} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-32" onClick={(e) => e.stopPropagation()}>
                      {PROJECT_MARKS.map((mark) => (
                        <DropdownMenuItem
                          key={mark}
                          onClick={(e) => handleMarkChange(project.id, mark, e)}
                          className={`cursor-pointer ${localMarks[project.id] === mark ? 'bg-accent' : ''}`}
                        >
                          <Flag className={`h-3.5 w-3.5 mr-2 ${
                            mark === 'prio_1' ? 'text-red-500 fill-red-500' :
                            mark === 'prio_2' ? 'text-orange-500 fill-orange-500' :
                            'text-slate-400 fill-slate-400'
                          }`} />
                          {PROJECT_MARK_LABELS[mark]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  localMarks[project.id] !== 'decent' ? (
                    <Flag className={`h-3 w-3 ${
                      localMarks[project.id] === 'prio_1' ? 'text-red-500 fill-red-500' :
                      localMarks[project.id] === 'prio_2' ? 'text-orange-500 fill-orange-500' :
                      'text-muted-foreground/50'
                    }`} />
                  ) : (
                    <Flag className="h-3 w-3 text-muted-foreground/30" />
                  )
                )}

                {canSetMark ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-0.5 rounded hover:bg-muted/50 transition-colors flex items-center gap-1"
                        title="Năm hợp đồng"
                      >
                        <Calendar className="h-3 w-3 text-muted-foreground/60" />
                        <span className="text-[10px] text-muted-foreground">
                          {localYears[project.id] || '—'}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-40" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={(e) => handleYearChange(project.id, null, e)}
                        className={`cursor-pointer ${!localYears[project.id] ? 'bg-accent' : ''}`}
                      >
                        Không gán năm
                      </DropdownMenuItem>
                      {contractYears.map((year) => (
                        <DropdownMenuItem
                          key={year}
                          onClick={(e) => handleYearChange(project.id, year, e)}
                          className={`cursor-pointer ${localYears[project.id] === year ? 'bg-accent' : ''}`}
                        >
                          {year}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  localYears[project.id] ? (
                    <span className="text-[10px] text-muted-foreground">{localYears[project.id]}</span>
                  ) : null
                )}

                {project.company ? (
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] px-2 py-0.5 font-medium rounded-md border ${COMPANY_COLORS[project.company] || 'bg-secondary/50 text-secondary-foreground border-secondary'}`}
                  >
                    {project.company}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground/50">—</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

function ProjectLogo({ imageUrl, projectName }: { imageUrl?: string | null; projectName: string }) {
  const [failedStage, setFailedStage] = useState<'none' | 'transform' | 'raw'>('none');

  if (!imageUrl || failedStage === 'raw') {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Building2 className="w-12 h-12 text-muted-foreground/30 group-hover:text-muted-foreground/50 transition-colors" />
      </div>
    );
  }

  const src =
    failedStage === 'transform'
      ? imageUrl
      : getTransformedPublicUrl(imageUrl, { bucket: 'project-images', width: 512, height: 384, quality: 80, format: 'webp' });

  return (
    <img
      src={src}
      alt={projectName}
      loading="lazy"
      decoding="async"
      className="relative w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
      onError={() => {
        if (failedStage === 'none') {
          setFailedStage('transform');
        } else if (failedStage === 'transform') {
          setFailedStage('raw');
        }
      }}
    />
  );
}
